import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { saveDoorCode, verifyPin } from '@/lib/api';
import { Shield, Lock, Plus, Trash2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const FIELDS = [
  { key: 'housekeeping_code', label: 'Housekeeping Code' },
  { key: 'guest_code', label: 'Guest Code' },
  { key: 'backup_code_1', label: 'Backup Code 1' },
  { key: 'backup_code_2', label: 'Backup Code 2' },
];

// Local id generator — keeps custom-code list keys stable across renders
// without pulling in a UUID dep. Prefixed so we can tell IDs apart in logs.
const makeId = () => `cc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * DoorCodeEditDialog — one modal with tabs for regular codes + PIN-gated admin code.
 * Keeps the same save/PIN semantics as the standalone Door Codes page.
 */
export function DoorCodeEditDialog({ open, onOpenChange, unit, existing, onSaved }) {
  const [form, setForm] = useState({
    housekeeping_code: '', housekeeping_code_note: '',
    guest_code: '', guest_code_note: '',
    backup_code_1: '', backup_code_1_note: '',
    backup_code_2: '', backup_code_2_note: '',
  });
  const [adminForm, setAdminForm] = useState({ admin_code: '', admin_code_note: '' });
  const [customCodes, setCustomCodes] = useState([]); // [{ id, label, value, note }]
  const [saving, setSaving] = useState(false);
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinInput, setPinInput] = useState('');

  useEffect(() => {
    if (open) {
      setForm({
        housekeeping_code: existing?.housekeeping_code || '',
        housekeeping_code_note: existing?.housekeeping_code_note || '',
        guest_code: existing?.guest_code || '',
        guest_code_note: existing?.guest_code_note || '',
        backup_code_1: existing?.backup_code_1 || '',
        backup_code_1_note: existing?.backup_code_1_note || '',
        backup_code_2: existing?.backup_code_2 || '',
        backup_code_2_note: existing?.backup_code_2_note || '',
      });
      setAdminForm({
        admin_code: existing?.admin_code || '',
        admin_code_note: existing?.admin_code_note || '',
      });
      // Backfill missing ids so React keys stay stable on legacy rows.
      setCustomCodes((existing?.custom_codes || []).map((c) => ({
        id: c.id || makeId(),
        label: c.label || '',
        value: c.value || '',
        note: c.note || '',
      })));
      setAdminUnlocked(false);
    }
  }, [open, existing]);

  const addCustomCode = () => setCustomCodes((rows) => [...rows, { id: makeId(), label: '', value: '', note: '' }]);
  const updateCustomCode = (id, patch) => setCustomCodes((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeCustomCode = (id) => setCustomCodes((rows) => rows.filter((r) => r.id !== id));

  const handleSave = async () => {
    if (!unit) return;
    // Drop empty custom rows (no label + no value) so the doc stays clean.
    const cleanedCustom = customCodes
      .filter((c) => (c.label || '').trim() || (c.value || '').trim() || (c.note || '').trim())
      .map((c) => ({ id: c.id, label: c.label.trim(), value: c.value.trim(), note: c.note.trim() }));
    setSaving(true);
    try {
      await saveDoorCode({
        unit_id: unit.id,
        property_id: unit.property_id,
        // Always preserve the current admin code (only changed if adminUnlocked)
        admin_code: adminUnlocked ? adminForm.admin_code : (existing?.admin_code || ''),
        admin_code_note: adminUnlocked ? adminForm.admin_code_note : (existing?.admin_code_note || ''),
        ...form,
        custom_codes: cleanedCustom,
      });
      toast.success('Door codes saved');
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePinVerify = async () => {
    try {
      const res = await verifyPin({ pin: pinInput, pin_type: 'shared' });
      if (res.valid) {
        setAdminUnlocked(true);
        setPinDialogOpen(false);
        setPinInput('');
        toast.success('Admin code unlocked');
      } else {
        toast.error('Incorrect PIN');
      }
    } catch {
      toast.error('Verification failed');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Door Codes — Unit {unit?.unit_number}</DialogTitle>
            <DialogDescription>Edit entry codes for this unit.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-3">
            {FIELDS.map(({ key, label }) => (
              <div key={key} className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{label}</Label>
                  <Input
                    value={form[key] || ''}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    data-testid={`door-code-${key}-input`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Note</Label>
                  <Input
                    value={form[`${key}_note`] || ''}
                    onChange={e => setForm(f => ({ ...f, [`${key}_note`]: e.target.value }))}
                    placeholder="Optional note"
                  />
                </div>
              </div>
            ))}

            {/* Admin section — PIN gated */}
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800">
                  <Shield className="h-3.5 w-3.5" /> Admin Code
                </div>
                {!adminUnlocked ? (
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPinDialogOpen(true)} data-testid="door-code-unlock-admin-btn">
                    <Lock className="h-3 w-3 mr-1" /> Unlock to edit
                  </Button>
                ) : (
                  <span className="text-[11px] text-emerald-700 font-medium">Unlocked</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Admin Code</Label>
                  <Input
                    value={adminUnlocked ? (adminForm.admin_code || '') : (existing?.admin_code ? '****' : '')}
                    onChange={e => setAdminForm(f => ({ ...f, admin_code: e.target.value }))}
                    disabled={!adminUnlocked}
                    data-testid="door-code-admin_code-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Note</Label>
                  <Input
                    value={adminUnlocked ? (adminForm.admin_code_note || '') : (existing?.admin_code_note || '')}
                    onChange={e => setAdminForm(f => ({ ...f, admin_code_note: e.target.value }))}
                    disabled={!adminUnlocked}
                    placeholder="Optional note"
                  />
                </div>
              </div>
            </div>

            {/* Custom code sections — free-form categories. Users can add
                as many extra codes as they want (e.g. "Gate", "Pool",
                "Storage") and delete them independently. The built-in
                Housekeeping / Guest / Backup / Admin sections above are
                unaffected. */}
            <div className="rounded-lg border border-teal-200 bg-teal-50/30 p-3 space-y-3" data-testid="door-code-custom-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-teal-800">
                  <KeyRound className="h-3.5 w-3.5" /> Additional Codes
                  {customCodes.length > 0 && (
                    <span className="text-[10px] text-teal-700/70 font-normal">({customCodes.length})</span>
                  )}
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-teal-300 text-teal-800 hover:bg-teal-100" onClick={addCustomCode} data-testid="door-code-add-custom-btn">
                  <Plus className="h-3 w-3" /> Add code
                </Button>
              </div>
              {customCodes.length === 0 ? (
                <p className="text-[11px] text-teal-800/70 italic">
                  No additional codes. Use this to add gate, pool, storage, or any custom access code with its own label.
                </p>
              ) : (
                <div className="space-y-2">
                  {customCodes.map((row) => (
                    <div key={row.id} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end" data-testid="door-code-custom-row">
                      <div className="space-y-1">
                        <Label className="text-[11px]">Label *</Label>
                        <Input
                          value={row.label}
                          onChange={(e) => updateCustomCode(row.id, { label: e.target.value })}
                          placeholder="e.g. Gate"
                          className="h-8 text-sm"
                          data-testid="door-code-custom-label"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px]">Code</Label>
                        <Input
                          value={row.value}
                          onChange={(e) => updateCustomCode(row.id, { value: e.target.value })}
                          placeholder="e.g. 4820#"
                          className="h-8 text-sm font-mono"
                          data-testid="door-code-custom-value"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px]">Note</Label>
                        <Input
                          value={row.note}
                          onChange={(e) => updateCustomCode(row.id, { note: e.target.value })}
                          placeholder="Optional"
                          className="h-8 text-sm"
                          data-testid="door-code-custom-note"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                        onClick={() => removeCustomCode(row.id)}
                        title="Delete this code section"
                        data-testid="door-code-custom-remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="door-code-save-btn">
              {saving ? 'Saving...' : 'Save Codes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIN verify sub-dialog */}
      <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle><Lock className="h-4 w-4 inline mr-2" />Enter PIN</DialogTitle>
            <DialogDescription>Enter the PIN to edit the admin code.</DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <Input
              type="password"
              placeholder="Enter PIN"
              value={pinInput}
              onChange={e => setPinInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handlePinVerify()}
              data-testid="door-code-pin-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPinDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePinVerify} data-testid="door-code-pin-verify-btn">Verify</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
